import { ICellEditorParams } from '@ag-grid-community/core'
import { LoadingOutlined } from '@ant-design/icons'
import { Button, Divider, Typography } from 'antd'
import Select, { LabeledValue, SelectProps } from 'antd/lib/select'
import { isNil, uniqBy } from 'lodash'
import React, { useCallback, useContext, useImperativeHandle, useMemo, useState } from 'react'
import { Basket, BasketType, useBasketsListQuery } from '../../../../api'
import { useDebounce } from '../../../components/useDebounce'
import { EditingAgGridContext } from '../MarkupRules'

export const MarketBasketSelector: React.FunctionComponent<SelectProps<LabeledValue[]>> = ({ style, ...props }) => {
  const [searchText, setSearchText] = useState('')
  const debounceSearchtext = useDebounce(searchText, 800)
  const { data, loading } = useBasketsListQuery({
    variables: {
      input: {
        searchText: debounceSearchtext,
        type: BasketType.Public,
      },
      pagination: { offset: 0, limit: 20 },
    },
    skip: isNil(debounceSearchtext),
  })

  const baskets = useMemo(() => {
    return uniqBy(
      [
        ...(props.value ? [props.value].flat() : []),
        ...(data?.basketsList?.hits || []).map(b => ({ label: b.name, value: b.id })),
      ],
      'value',
    )
  }, [data?.basketsList, props.value])

  const dropdownRender = useCallback(
    (menu: React.ReactNode) => {
      return (
        <>
          {menu}
          {loading && (
            <Typography.Text style={{ margin: '0px 0 0px 8px' }} type="secondary">
              Searching ...
            </Typography.Text>
          )}
          {data?.basketsList && data?.basketsList.totalHits > data?.basketsList.hits.length && (
            <>
              <Divider style={{ margin: '8px 0' }} />
              <Button type="link" style={{ whiteSpace: 'nowrap', padding: '0 8px 4px' }}>
                There are {data?.basketsList.totalHits - data?.basketsList.hits.length} more results available
              </Button>
            </>
          )}
        </>
      )
    },
    [data?.basketsList, loading],
  )

  return (
    <Select
      showSearch
      onSearch={setSearchText}
      filterOption={false}
      showArrow={false}
      defaultActiveFirstOption={false}
      dropdownMatchSelectWidth={false}
      style={{ width: '100%', ...style }}
      loading={loading}
      clearIcon={loading ? <LoadingOutlined spin /> : undefined}
      notFoundContent={null}
      dropdownRender={dropdownRender}
      mode="multiple"
      placeholder="Select Market Basket"
      {...props}>
      {baskets.map(i => (
        <Select.Option key={i.value} value={i.value} label={i.label}>
          {i.label}
        </Select.Option>
      ))}
    </Select>
  )
}

export const AgMarketBasketSelector = React.forwardRef<{}, ICellEditorParams>((params, ref) => {
  const [value, setValue] = useState<LabeledValue[]>(
    params.data.baskets?.map((i: Basket) => ({ label: i.name, value: i.id })) || [],
  )
  const { onChangeEditingItem, editingItem } = useContext(EditingAgGridContext)

  useImperativeHandle(ref, () => ({
    getValue: () => value,
  }))

  return (
    <MarketBasketSelector
      disabled={!!editingItem?.manufacturer?.length || !!editingItem?.unspsc?.length}
      style={{
        visibility: !!editingItem?.manufacturer?.length || !!editingItem?.unspsc?.length ? 'hidden' : 'visible',
      }}
      allowClear
      value={value}
      onChange={v => {
        onChangeEditingItem(prev => ({
          ...prev,
          marketBasket: v.map((i: LabeledValue) => i.value as string),
          baskets: v.map((i: LabeledValue) => ({ id: i.value as string, name: i.label as string })) as Basket[],
        }))
        setValue(v)
      }}
      labelInValue
      maxTagCount="responsive"
    />
  )
})
